import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSelector } from "react-redux";
import { stripePromise } from "../../constants/constants";
import { fetchAllCohorts } from "../../services/adminService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { api1 } from "../../utils/axiosInstance";
import StatusModal from "../StatusModal/StatusModal";
import WelcomeCard from "../WelcomeCard";
import GroupMentoringCards from "./GroupMentoringCards/GroupMentoringCards";
import { DateTime } from "luxon";

// Create a separate component for the payment form that uses Stripe hooks
const PaymentForm = ({ cohort, onCancel, onSuccess, userId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const handlePaymentSubmit = async () => {
    if (!cohort || !stripe || !elements) return;

    setPaymentProcessing(true);
    try {
      const { data: clientSecret } = await api1.post(
        "/api/stripe/create-payment-intent",
        {
          amount: cohort.price * 100,
          currency: "usd",
          metadata: {
            cohortId: cohort.id,
            userId: userId,
          },
        }
      );

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (error) {
        console.error("Payment error:", error);
        alert(`Payment failed: ${error.message}`);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        await api1.post(`${API_ENDPOINTS.REGISTER_CHORTS}/${cohort.id}`, {
          userId,
        });
        onSuccess();
      }
    } catch (error) {
      console.error("Error during payment process:", error);
      onSuccess();
    } finally {
      setPaymentProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-bold mb-4">
          Complete Payment for {cohort.mentorName}
        </h3>
        <p className="mb-4">Amount: ${cohort.price.toFixed(2)}</p>

        <div className="mb-4 p-4 border rounded">
          <CardElement options={{ hidePostalCode: true }} />
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border rounded"
            disabled={paymentProcessing}
          >
            Cancel
          </button>
          <button
            onClick={handlePaymentSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded"
            disabled={paymentProcessing || !stripe}
          >
            {paymentProcessing ? "Processing..." : "Pay Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

const MentorGroupsPage = () => {
  const [cohort, setCohort] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "",
    message: "",
  });
  const queryClient = useQueryClient();

  const userId = useSelector((state) => state.auth.id);
  const {
    data: fetchCohorts,
    isLoading: fetchCohortsPending,
    error: fetchCohortsError,
  } = useQuery({
    queryKey: ["getCohorts", userId],
    queryFn: fetchAllCohorts,
    enabled: !!userId,
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const formatFrequency = (frequency) => {
    return frequency === "weekly" ? "Weekly" : "Biweekly";
  };

 const mentoringPrograms = fetchCohorts?.length
  ? fetchCohorts.map((program) => {
      const start = DateTime.fromISO(program.startDate, { zone: "utc" }).setZone(program.timezone);
      const end = DateTime.fromISO(program.endDate, { zone: "utc" }).setZone(program.timezone);

      return {
        id: program._id,
        description: program.description,
        mentorName: program.mentor.name,
        startDate: start.toFormat("MMMM d, yyyy"),
        endDate: end.toFormat("MMMM d, yyyy"),
        dateTime: `${start.toFormat("cccc")}, ${start.toFormat("hh:mm a")} - ${end.toFormat("hh:mm a")}`,
        timezone: program.timezone,
        frequency: formatFrequency(program.frequency),
        price: program.pricePerMentee,
        seatsAvailable: program.seatsAvailable,
        totalSeats: program.seatsAvailable,
        registeredUsers: program.registeredUsers,
        targetUsers: program.targetUsers,
        whatUserWillGetOutOfThis: program.whatUserWillGetOutOfThis
      };
    })
  : [];

  const handleClick = async (cohort) => {
    setCohort(cohort);
  };

  const handlePaymentSuccess = () => {
    queryClient.invalidateQueries(["getCohorts", userId]);
    setStatusModal({
      open: true,
      type: "success",
      message:
        "Payment successful! You are now registered for the group mentoring.",
    });
    setCohort(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <StatusModal
        isOpen={statusModal.open}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
        type={statusModal.type}
        message={statusModal.message}
      />
      <WelcomeCard title={"Mentoring Group"} />
      <GroupMentoringCards
        mentoringPrograms={mentoringPrograms}
        onClick={handleClick}
      />
      {/* Payment Modal */}
      {cohort && (
        <Elements stripe={stripePromise}>
          <PaymentForm
            cohort={cohort}
            onCancel={() => setCohort(null)}
            onSuccess={handlePaymentSuccess}
            userId={userId}
          />
        </Elements>
      )}
    </div>
  );
};

export default MentorGroupsPage;
